import Button from '@components/Button';
import Table from '@components/Table';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@services/api';
import { useHistory } from 'react-router-dom';
import Modal from '@components/Modal';
import Moment from 'react-moment';

const permissions = ['administrador'];
const linkbackend = process.env.REACT_APP_BASE_IMAGE_URL;

// ── Hook: largura da janela ───────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

// ── Dropdown de opções (responsivo) ──────────────────────────────────────────
const OptionsMenu = ({ onChoose, onClose }) => {
  const menuRef = useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [onClose]);

  const options = [
    { id: '1', icon: 'send',       label: 'Enviar' },
    { id: '2', icon: 'visibility', label: 'Visualizar' },
    { id: '3', icon: 'delete',     label: 'Excluir', danger: true },
  ];

  return (
    <div
      ref={menuRef}
      style={{
        position: 'absolute',
        right: 0,
        top: '100%',
        zIndex: 9999,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        minWidth: 160,
        overflow: 'hidden',
      }}
    >
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => { onChoose(opt.id); onClose(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: '12px 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: opt.danger ? '#ef4444' : '#172B4D',
            fontSize: 14,
            textAlign: 'left',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <Icon style={{ fontSize: 16, color: opt.danger ? '#ef4444' : '#64748b' }}>{opt.icon}</Icon>
          {opt.label}
        </button>
      ))}
    </div>
  );
};

// ── Card de mensagem (mobile) ─────────────────────────────────────────────────
const MessageCard = ({ row, onAction, openMenuId, setOpenMenuId, theme }) => {
  const isOpen = openMenuId === row.id;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      padding: '14px 16px',
      marginBottom: 10,
      position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
          <p style={{ fontWeight: 600, fontSize: 14, color: '#172B4D', marginBottom: 4, wordBreak: 'break-word' }}>
            {row.template_name}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#64748b' }}>
            {row.user_name && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon style={{ fontSize: 13 }}>person</Icon> {row.user_name}
              </span>
            )}
            {row.date_send && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon style={{ fontSize: 13 }}>event</Icon>
                <Moment format="DD/MM/YYYY" date={row.date_send} />
              </span>
            )}
          </div>
        </div>

        {/* Botão de opções */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setOpenMenuId(isOpen ? null : row.id)}
            style={{
              background: isOpen ? '#f1f5f9' : 'none',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 13,
              color: '#172B4D',
            }}
          >
            <Icon style={{ fontSize: 16 }}>more_vert</Icon>
          </button>
          {isOpen && (
            <OptionsMenu
              onChoose={(id) => onAction(id, row)}
              onClose={() => setOpenMenuId(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Tela principal ────────────────────────────────────────────────────────────
const Index = (props) => {
  document.title = `Mensagens - ${process.env.REACT_APP_NAME}`;
  const history   = useHistory();
  const width     = useWindowWidth();
  const isMobile  = width < 768;

  const [rows]                    = useState([]);
  const [refresh, setRefresh]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Modal de detalhes
  const [openModal, setOpenModal]         = useState(false);
  const [messageDetails, setMessageDetails] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState({});
  const [loadingDetails, setLoadingDetails]   = useState(false);

  // Lista para cards mobile
  const [mobileRows, setMobileRows] = useState([]);

  useEffect(() => { props.setShowMenu(false); }, []);

  // Carrega mensagens para o modo mobile
  useEffect(() => {
    if (!isMobile) return;
    apiClient.httpInstance.get('/template_messages?paginated=true')
      .then(res => {
        const data = res.data?.data ?? res.data ?? [];
        setMobileRows(Array.isArray(data) ? data : data.data ?? []);
      })
      .catch(() => {});
  }, [isMobile, refresh]);

  const checkContact = async (customer_receiver) => {
    const resp = await apiClient.httpInstance.get(`/central/check_contact?customer_receiver=${customer_receiver}`);
    return !!(resp?.data?.name);
  };

  const showMessageDetails = (send) => {
    setSelectedMessage(send);
    setMessageDetails([]);
    setLoadingDetails(true);
    setOpenModal(true);

    const date      = new Date(send.created_at);
    const created_at = new Date(date.setTime(date.getTime() - 3 * 60 * 60 * 1000)).toJSON('pt-BR', { timeZone: 'America/Sao_Paulo' });

    apiClient.httpInstance
      .get(`/central/message_details?template_id=${send.template_id}&user_sender=${send.user_sender}&created_at=${created_at}`)
      .then(async (res) => {
        const messages = res.data.data;
        for (let i = 0; i < messages.length; i++) {
          messages[i].checkContact = await checkContact(messages[i].customer_receiver);
        }
        setMessageDetails(messages);
        setLoadingDetails(false);
      })
      .catch(() => setLoadingDetails(false));
  };

  const verifyMessages = (receiver, template, single, deleteM) => {
    setLoading(true);
    apiClient.httpInstance.post('/central/send_messages', {
      delete_message: deleteM ? 'sim' : false,
      template_id:       template  || false,
      single_message:    single    || false,
      customer_receiver: receiver  || false,
    }).then(() => {
      setRefresh(r => !r);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const onAction = (action, row) => {
    if (action === '1') verifyMessages(false, row?.template_id, true);
    else if (action === '2') showMessageDetails(row);
    else if (action === '3') verifyMessages(false, row?.id, true, true);
  };

  return (
    <div className="px-4 md:px-7 pb-10">

      {/* ── Header ── */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 8,
        marginBottom: 16,
        color: props.theme.color,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 22, fontWeight: 600 }}>
          <Icon className="mr-2">accessibility</Icon>
          <span>Lista de mensagens</span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            textcolor="#fff"
            padding="6px 15px"
            margintop="0px"
            width="180"
            onClick={() => verifyMessages()}
            customcolor={props.theme.color}
          >
            {loading && !openModal
              ? <CircularProgress color="inherit" size={15} />
              : 'Realizar Envios'}
          </Button>

          {permissions.includes(props?.theme?.permission) && (
            <Button
              textcolor="#fff"
              padding="6px 15px"
              margintop="0px"
              width="130"
              onClick={() => history.push('/admin/central/mensagens/cadastro')}
              customcolor={props.theme.color}
            >
              <Icon className="mr-2">add</Icon>
              Novo
            </Button>
          )}
        </div>
      </div>

      {/* ── Lista ── */}
      {isMobile ? (
        /* Cards mobile */
        <div>
          {mobileRows.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '48px 0' }}>
              <Icon style={{ fontSize: 40 }}>mail_outline</Icon>
              <p style={{ marginTop: 8 }}>Nenhuma mensagem encontrada.</p>
            </div>
          )}
          {mobileRows.map(row => (
            <MessageCard
              key={row.id}
              row={row}
              onAction={onAction}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              theme={props.theme}
            />
          ))}
        </div>
      ) : (
        /* Tabela desktop */
        <Table
          refresh={refresh}
          path="/template_messages"
          onRowClick={null}
          columns={[
            {
              headerName: 'Template',
              headerClassName: 'super-app-theme--header',
              field: 'template_name',
              flex: 1,
              renderCell: (params) => {
                const row = params.row;
                return (
                  <div className="w-full h-full cursor-pointer" onClick={() => showMessageDetails(row)}>
                    {row.template_name}
                  </div>
                );
              },
            },
            {
              headerName: 'Enviado em',
              headerClassName: 'super-app-theme--header',
              field: 'date_send',
              flex: 0.5,
              renderCell: (params) => (
                <div className="w-full h-full">
                  {params.row.date_send
                    ? <Moment format="DD/MM/YYYY" date={params.row.date_send} />
                    : '--/--/--'}
                </div>
              ),
            },
            {
              headerName: 'Enviado por',
              headerClassName: 'super-app-theme--header',
              field: 'user_name',
              flex: 1,
              renderCell: (params) => (
                <div className="w-full h-full cursor-pointer" onClick={() => showMessageDetails(params.row)}>
                  {params.row.user_name}
                </div>
              ),
            },
            {
              headerName: 'Opções',
              headerClassName: 'super-app-theme--header',
              field: 'sent',
              flex: 0.4,
              renderCell: (params) => {
                const row     = params.row;
                const isOpen  = openMenuId === row.id;
                return (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setOpenMenuId(isOpen ? null : row.id)}
                      style={{
                        background: isOpen ? '#f1f5f9' : 'none',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#172B4D',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <Icon style={{ fontSize: 16 }}>more_vert</Icon>
                      Opções
                    </button>
                    {isOpen && (
                      <OptionsMenu
                        onChoose={(id) => { onAction(id, row); setOpenMenuId(null); }}
                        onClose={() => setOpenMenuId(null)}
                      />
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={rows}
        />
      )}

      {/* ── Modal de detalhes ── */}
      <Modal open={openModal} style={{ width: '95%', maxWidth: 650 }}>
        <div style={{ padding: '4px 0 12px' }}>
          <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
            Template: {selectedMessage.template_name}
          </p>
          {(selectedMessage.url_link || selectedMessage.url_video || selectedMessage.url_document || selectedMessage.url_image) && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              {selectedMessage.url_link     && <a href={linkbackend + '/' + selectedMessage.url_link}     target="_blank" rel="noreferrer" style={{ color: props.theme.color, fontSize: 13 }}>🔗 Link</a>}
              {selectedMessage.url_video    && <a href={linkbackend + '/' + selectedMessage.url_video}    target="_blank" rel="noreferrer" style={{ color: props.theme.color, fontSize: 13 }}>🎬 Vídeo</a>}
              {selectedMessage.url_document && <a href={linkbackend + '/' + selectedMessage.url_document} target="_blank" rel="noreferrer" style={{ color: props.theme.color, fontSize: 13 }}>📄 Documento</a>}
              {selectedMessage.url_image    && <a href={linkbackend + '/' + selectedMessage.url_image}    target="_blank" rel="noreferrer" style={{ color: props.theme.color, fontSize: 13 }}>🖼 Imagem</a>}
            </div>
          )}
        </div>

        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>Envios:</p>

        {loadingDetails && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <CircularProgress size={20} />
          </div>
        )}

        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {messageDetails.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 4px',
                borderBottom: '1px solid #f1f5f9',
                fontSize: 13,
                gap: 8,
              }}
            >
              <span style={{ flex: 1, wordBreak: 'break-word' }}>
                {msg.customer_name}
              </span>
              <span style={{ color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {msg.date_send ? <Moment format="DD/MM/YYYY" date={msg.date_send} /> : '--/--/--'}
              </span>
            </div>
          ))}
          {!loadingDetails && messageDetails.length === 0 && (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: '16px 0', fontSize: 13 }}>
              Nenhum envio encontrado.
            </p>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <Button
            textcolor="#fff"
            padding="10px 15px"
            margintop="0px"
            width="100%"
            onClick={() => setOpenModal(false)}
            customcolor={props.theme.color}
          >
            Fechar
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Index;
